import { Button } from '@crehana/compass.ui/v3/button';
import { Textarea } from '@crehana/compass.ui/v3/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { useForm, zodResolver } from '@crehana/forms/rhf';
import { z } from 'zod';

import { useTranslation } from '../../../i18n';

const COMMENT_MAX_LENGTH = 150;

type TCommentFormValues = {
  comment: string;
};

type TCommentFormProps = {
  onSubmit: (body: string) => void;
  submitting: boolean;
};

// AC-4 (write). No mutation exists for TIM-557 yet — a submitted comment
// lives only in the session store `onSubmit` writes to (plan Risk R1), so
// this form shows no success toast: that would tell the collaborator the
// comment reached a server.
export const CommentForm = ({ onSubmit, submitting }: TCommentFormProps) => {
  const { t } = useTranslation();

  // `t` is not stable across renders: `useTranslation` builds it as a new
  // arrow on every call. Rebuild the schema every render instead of
  // memoizing it, so a live locale change reaches the validation messages.
  // Same reason as `respond-request-dialog.tsx` in TIM-562.
  const schema = z.object({
    comment: z
      .string()
      .trim()
      .min(1, t('VALIDATION_REQUIRED'))
      .max(COMMENT_MAX_LENGTH, t('VALIDATION_COMMENT_MAX_LENGTH')),
  });

  const form = useForm<TCommentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { comment: '' },
  });

  const handleSubmit = ({ comment }: TCommentFormValues) => {
    onSubmit(comment);
    form.reset();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw-flex tw-flex-col tw-cds-gap-2"
      >
        <FormField
          control={form.control}
          name="comment"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('REQUEST_DETAIL_COMMENT_LABEL')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  maxLength={COMMENT_MAX_LENGTH}
                  error={Boolean(fieldState.error)}
                  placeholder={t('REQUEST_DETAIL_COMMENT_PLACEHOLDER')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="tw-flex tw-justify-start">
          <Button
            type="submit"
            variant="tertiary"
            size="sm"
            loading={submitting}
          >
            {t('REQUEST_DETAIL_COMMENT_SUBMIT')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
